import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaGift, FaCheck, FaTimes, FaWallet, FaClock, FaFilter } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../api/axios';

const OPERATOR_LABEL = {
  orange_money: 'Orange Money',
  mtn_momo:    'MTN MoMo',
  wave:        'Wave',
};

const STATUS_CONFIG = {
  pending:  { label: 'En attente', color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/30' },
  paid:     { label: 'Payé',       color: 'text-green-400',  bg: 'bg-green-500/15',  border: 'border-green-500/30'  },
  rejected: { label: 'Rejeté',    color: 'text-red-400',    bg: 'bg-red-500/15',    border: 'border-red-500/30'    },
};

function AdminReferral() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState('pending');
  const [processing, setProcessing]   = useState(null);
  const [rejectNote, setRejectNote]   = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);

  const load = (status = filter) => {
    setLoading(true);
    api.get(`/admin/referral/withdrawals?status=${status}`)
      .then(({ data }) => setWithdrawals(data.withdrawals || []))
      .catch(() => setWithdrawals([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); }, [filter]);

  const handlePay = async (id) => {
    if (!window.confirm('Confirmer le paiement de ce retrait ?')) return;
    setProcessing(id);
    try {
      await api.put(`/admin/referral/withdrawals/${id}`, { status: 'paid' });
      toast.success('Retrait marqué comme payé ✅');
      setWithdrawals((ws) => ws.filter((w) => w.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectTarget) return;
    setProcessing(rejectTarget);
    try {
      await api.put(`/admin/referral/withdrawals/${rejectTarget}`, {
        status: 'rejected',
        note: rejectNote || undefined,
      });
      toast('Retrait rejeté — solde remboursé', { icon: '↩️' });
      setWithdrawals((ws) => ws.filter((w) => w.id !== rejectTarget));
      setRejectTarget(null);
      setRejectNote('');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });

  const totalPending = withdrawals.filter((w) => w.status === 'pending')
    .reduce((sum, w) => sum + w.amount_fcfa, 0);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-black text-white uppercase tracking-widest"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          Parrainage — Retraits
        </h1>
        <p className="text-white/40 text-sm">Traitement des demandes de retrait des parrains</p>
      </div>

      {/* Résumé si en attente */}
      {filter === 'pending' && withdrawals.length > 0 && (
        <div className="rounded-2xl p-4 flex items-center gap-4"
          style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.25)' }}>
          <FaWallet size={22} className="text-yellow-400 flex-shrink-0" />
          <div>
            <p className="text-yellow-400 font-black text-lg" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              {totalPending.toLocaleString('fr-CI')} FCFA à verser
            </p>
            <p className="text-white/40 text-xs">{withdrawals.length} demande(s) en attente</p>
          </div>
        </div>
      )}

      {/* Filtre */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'pending',  label: 'En attente', Icon: FaClock  },
          { key: 'paid',     label: 'Payés',       Icon: FaCheck  },
          { key: 'rejected', label: 'Rejetés',     Icon: FaTimes  },
          { key: 'all',      label: 'Tous',        Icon: FaFilter },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold transition-all"
            style={{
              fontFamily: "'Rajdhani', sans-serif",
              background: filter === key ? 'rgba(255,184,28,0.15)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${filter === key ? 'rgba(255,184,28,0.4)' : 'rgba(255,255,255,0.1)'}`,
              color: filter === key ? '#FFB81C' : 'rgba(255,255,255,0.5)',
            }}
          >
            <Icon size={12} />
            {label}
          </button>
        ))}
      </div>

      {/* Modal rejet */}
      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setRejectTarget(null)} />
          <div className="relative w-full max-w-sm rounded-2xl p-5"
            style={{ background: '#001a4d', border: '1px solid rgba(239,68,68,0.3)' }}>
            <h3 className="text-red-400 font-black uppercase text-sm mb-3 flex items-center gap-2"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              <FaTimes size={14} /> Rejeter le retrait
            </h3>
            <p className="text-white/50 text-xs mb-3">
              Le solde sera remboursé automatiquement au parrain.
            </p>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="Motif du rejet (optionnel)"
              className="form-input w-full text-sm resize-none mb-3"
              rows={3}
            />
            <div className="flex gap-2">
              <button
                onClick={() => { setRejectTarget(null); setRejectNote(''); }}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm uppercase text-white/50 hover:text-white/70 transition-colors"
                style={{ background: 'rgba(255,255,255,0.06)', fontFamily: "'Rajdhani', sans-serif" }}
              >
                Annuler
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={!!processing}
                className="flex-1 py-2.5 rounded-xl font-black text-sm uppercase disabled:opacity-50 transition-opacity"
                style={{ background: 'rgba(239,68,68,0.8)', color: 'white', fontFamily: "'Rajdhani', sans-serif" }}
              >
                {processing ? '...' : 'REJETER'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
        </div>
      ) : withdrawals.length === 0 ? (
        <div className="text-center py-12 text-white/30">
          <FaGift size={32} className="mx-auto mb-3 opacity-30" />
          <p style={{ fontFamily: "'Rajdhani', sans-serif" }}>Aucune demande</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {withdrawals.map((w, i) => {
            const sc = STATUS_CONFIG[w.status] || STATUS_CONFIG.pending;
            return (
              <motion.div
                key={w.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="rounded-2xl p-4"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <div className="flex items-start gap-3">
                  {/* Montant */}
                  <div className="flex-shrink-0 w-16 h-16 rounded-xl flex flex-col items-center justify-center"
                    style={{ background: 'rgba(255,184,28,0.08)', border: '1px solid rgba(255,184,28,0.2)' }}>
                    <span className="text-qfl-gold font-black text-lg leading-none"
                      style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                      {(w.amount_fcfa / 1000).toFixed(0)}k
                    </span>
                    <span className="text-white/40 text-xs">FCFA</span>
                  </div>

                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-white font-black text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                        {w.pseudo}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold border ${sc.bg} ${sc.color} ${sc.border}`}
                        style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                        {sc.label}
                      </span>
                    </div>
                    <p className="text-white/50 text-xs">{w.email}</p>
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      <span className="text-white/60 text-xs font-bold"
                        style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                        {OPERATOR_LABEL[w.operator] || w.operator}
                      </span>
                      <span className="text-white/40 text-xs font-mono tracking-wider">{w.phone}</span>
                      <span className="text-white/30 text-xs">{formatDate(w.created_at)}</span>
                    </div>
                    {w.note && (
                      <p className="text-red-400/70 text-xs mt-1 italic">Note : {w.note}</p>
                    )}
                  </div>

                  {/* Actions (seulement si pending) */}
                  {w.status === 'pending' && (
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => handlePay(w.id)}
                        disabled={processing === w.id}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl font-black text-xs uppercase disabled:opacity-50 transition-opacity"
                        style={{
                          background: 'linear-gradient(135deg, ##003DA5, #15803d)',
                          color: 'white',
                          fontFamily: "'Rajdhani', sans-serif",
                        }}
                      >
                        <FaCheck size={11} />
                        PAYÉ
                      </button>
                      <button
                        onClick={() => setRejectTarget(w.id)}
                        disabled={processing === w.id}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl font-black text-xs uppercase disabled:opacity-50 transition-opacity"
                        style={{
                          background: 'rgba(239,68,68,0.15)',
                          color: '#f87171',
                          border: '1px solid rgba(239,68,68,0.3)',
                          fontFamily: "'Rajdhani', sans-serif",
                        }}
                      >
                        <FaTimes size={11} />
                        REJETER
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AdminReferral;


